// Auth.js
import { useState, useRef, useEffect } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { supabase } from "./supabaseClient";

const RAILWAY_URL = "https://sqastudybuddy-production.up.railway.app";

// Google Ads conversion tracking helper
function trackSignupConversion() {
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-18142220512/2fqyCLv_lagcEOCh8cpD",
    });
  }
}

export default function Auth() {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);

useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("reset") === "true") {
    setMode("reset");
  }
}, []);

const resetCaptcha = () => {
  setCaptchaToken(null);
  turnstileRef.current?.reset();
};

  const handlePasswordReset = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setError(error.message);
    } else {
      setSuccess("Password updated! You can now sign in with your new password.");
      setMode("signin");
      setNewPassword("");
      window.history.replaceState(null, "", window.location.pathname);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    if (!email.trim() || (!password.trim() && mode !== "forgot")) return;

    if ((mode === "signup" || mode === "signin") && !captchaToken) {
      setError("Please complete the security check.");
      return;
    }

    setLoading(true);

    if (mode === "signup") {
      try {
        const response = await fetch(`${RAILWAY_URL}/signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, captchaToken })
        });
        const result = await response.json();
        if (!response.ok) {
          setError(result.error || "Something went wrong. Please try again.");
        } else if (result.alreadyExists) {
          setError("An account with this email already exists. Sign in instead.");
        } else {
          // ✅ Fire Google Ads conversion on successful signup
          trackSignupConversion();
          setSuccess("Account created! Check your inbox and click the confirmation link to activate your account.");
        }
      } catch (err) {
        setError("Connection error. Please try again.");
      }
      resetCaptcha();
    }

    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });
      if (error) {
        if (error.message.toLowerCase().includes("email not confirmed")) {
          setError("Please confirm your email first — check your inbox for a link from Summit.");
        } else {
          setError(error.message);
        }
      }
      resetCaptcha();
    }

    if (mode === "forgot") {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: "https://www.usesummit.co.uk/auth"
      });
      if (error) {
        setError(error.message);
      } else {
        setSuccess("Password reset email sent! Check your inbox and click the link.");
      }
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") {
      if (mode === "reset") handlePasswordReset();
      else handleSubmit();
    }
  };

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setError(null);
    setSuccess(null);
    resetCaptcha();
  };

  const cardStyle = {
    width: "100%",
    maxWidth: 400,
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: "36px 32px",
    animation: "fadeUp 0.5s ease 0.1s both"
  };

  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
    transition: "border-color 0.15s"
  };

  const labelStyle = {
    fontSize: 11,
    color: "rgba(240,244,242,0.4)",
    marginBottom: 6,
    letterSpacing: 0.5,
    fontWeight: 500
  };

  const btnStyle = (disabled) => ({
    width: "100%",
    padding: "13px",
    background: disabled ? "rgba(29,158,117,0.4)" : "#1D9E75",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 500,
    cursor: disabled ? "default" : "pointer",
    transition: "opacity 0.2s",
    marginBottom: 20
  });

  const wrapStyle = {
    minHeight: "100vh",
    background: "#080e0d",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans', sans-serif",
    padding: "24px"
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap');
    @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    input:focus { outline: none; border-color: #1D9E75 !important; }
    input { color: #f0f4f2 !important; }
    input::placeholder { color: rgba(240,244,242,0.25) !important; }
  `;

  // ── Password Reset Screen ─────────────────────────────────────────
  if (mode === "reset") {
    return (
      <div style={wrapStyle}>
        <style>{css}</style>
        <div style={cardStyle}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 28, color: "#f0f4f2", marginBottom: 6, letterSpacing: -0.5 }}>
            Set new password
          </div>
          <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", marginBottom: 28, fontWeight: 300 }}>
            Choose a new password for your account
          </div>

          <div style={{ marginBottom: 22 }}>
            <div style={labelStyle}>NEW PASSWORD</div>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              onKeyDown={handleKey}
              placeholder="At least 6 characters"
              style={inputStyle}
            />
          </div>

          {error && (
            <div style={{ padding: "10px 14px", background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: 8, fontSize: 13, color: "#E24B4A", marginBottom: 16, fontWeight: 300 }}>{error}</div>
          )}
          {success && (
            <div style={{ padding: "10px 14px", background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 8, fontSize: 13, color: "#4DC9A0", marginBottom: 16, fontWeight: 300 }}>{success}</div>
          )}

          <button
            onClick={handlePasswordReset}
            disabled={loading}
            style={btnStyle(loading)}
            onMouseOver={e => { if (!loading) e.currentTarget.style.opacity = "0.88"; }}
            onMouseOut={e => e.currentTarget.style.opacity = "1"}>
            {loading ? "Please wait…" : "Update password"}
          </button>

          <div style={{ textAlign: "center" }}>
            <button onClick={() => handleModeSwitch("signin")} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              ← Back to sign in
            </button>
          </div>
        </div>
        <div style={{ marginTop: 24, fontSize: 11, color: "rgba(240,244,242,0.15)", fontWeight: 300 }}>
          Built for Scottish students · © 2026 Summit
        </div>
      </div>
    );
  }

  // ── Main Auth Screen ──────────────────────────────────────────────
  return (
    <div style={wrapStyle}>
      <style>{css}</style>
      <div style={cardStyle}>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 28, color: "#f0f4f2", marginBottom: 6, letterSpacing: -0.5 }}>
          {mode === "signin" && "Welcome back"}
          {mode === "signup" && "Create your account"}
          {mode === "forgot" && "Reset your password"}
        </div>
        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.35)", marginBottom: 28, fontWeight: 300 }}>
          {mode === "signin" && "Sign in to continue studying"}
          {mode === "signup" && "Start free — no card required"}
          {mode === "forgot" && "We'll get you back on track"}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>EMAIL</div>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={handleKey}
            placeholder="you@example.com"
            style={inputStyle}
          />
        </div>

        {mode !== "forgot" && (
          <div style={{ marginBottom: 22 }}>
            <div style={labelStyle}>PASSWORD</div>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={handleKey}
              placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
              style={inputStyle}
            />
          </div>
        )}

        {mode !== "forgot" && (
          <div style={{ marginBottom: 16 }}>
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              onSuccess={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => {
                setCaptchaToken(null);
                setError("Security check failed. Please try again.");
              }}
              options={{ theme: "dark" }}
            />
          </div>
        )}

        {error && (
          <div style={{ padding: "10px 14px", background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: 8, fontSize: 13, color: "#E24B4A", marginBottom: 16, fontWeight: 300 }}>{error}</div>
        )}
        {success && (
          <div style={{ padding: "10px 14px", background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 8, fontSize: 13, color: "#4DC9A0", marginBottom: 16, fontWeight: 300 }}>{success}</div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={btnStyle(loading)}
          onMouseOver={e => { if (!loading) e.currentTarget.style.opacity = "0.88"; }}
          onMouseOut={e => e.currentTarget.style.opacity = "1"}>
          {loading ? "Please wait…" : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
        </button>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          {mode === "signin" && (
            <>
              <button onClick={() => handleModeSwitch("forgot")} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                Forgot your password?
              </button>
              <div style={{ fontSize: 13, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>
                Don't have an account?{" "}
                <button onClick={() => handleModeSwitch("signup")} style={{ background: "none", border: "none", color: "#4DC9A0", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, padding: 0 }}>
                  Sign up free
                </button>
              </div>
            </>
          )}
          {mode === "signup" && (
            <div style={{ fontSize: 13, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>
              Already have an account?{" "}
              <button onClick={() => handleModeSwitch("signin")} style={{ background: "none", border: "none", color: "#4DC9A0", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, padding: 0 }}>
                Sign in
              </button>
            </div>
          )}
          {mode === "forgot" && (
            <button onClick={() => handleModeSwitch("signin")} style={{ background: "none", border: "none", color: "rgba(240,244,242,0.3)", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              ← Back to sign in
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 24, fontSize: 11, color: "rgba(240,244,242,0.15)", fontWeight: 300 }}>
        Built for Scottish students · © 2026 Summit
      </div>
    </div>
  );
}